import { CAMERA_CATALOG } from './cameraCatalog';
import { UNIFI_CATALOG, UNIFI_ACCESS_READER_CATALOG } from './unifiCatalog';
import { FFE_EQUIPMENT_CATALOG } from './lowVoltageRates';

// Staff-editable price overrides for the four hardcoded hardware catalogs
// (buildlab_catalog_price_overrides, added 2026-07-30 — real staff request:
// "In the Admin CRM Main Page I need a Section Where all the hardware
// catalog pricing, rates, etc are available so I can update them as
// needed."). The catalogs themselves stay hardcoded JS arrays on purpose —
// they carry researched, dated, source-cited entries (specs/imageUrl/
// verification notes) that belong in code review, not a CMS; the ONLY thing
// staff actually needs to move is the price, which drifts constantly.
//
// WHY mutate-in-place instead of a context/store/prop-drilled overlay:
// CAMERA_CATALOG / UNIFI_CATALOG / UNIFI_ACCESS_READER_CATALOG /
// FFE_EQUIPMENT_CATALOG are module singletons — every consumer (pricing:
// estimateDeviceCost()/rackBuilderSummary()/ffeItemCost(); pickers:
// DeviceEditPanel's camera/AP/reader dropdowns, RackCatalogPicker, the FFE
// "+ Insert device" select; schedules: EquipmentDeviceSchedule/
// DeviceInspector) reads the SAME array objects by reference. One mutation
// pass over each entry's `priceUsd` therefore updates every one of those
// call sites at once with ZERO per-consumer changes — no threading a
// rateSettings-style prop through ~10 components and 4 data-layer pricing
// functions that today read the arrays directly at module scope. The one
// accepted limitation: a component already MOUNTED when an override lands
// won't re-render until its next state update (there's no subscription on a
// plain array). Fine in practice — edits happen on the CRM's Catalog
// Pricing tab, while pricing is consumed on OTHER pages (proposal builder,
// floor-plan editor, client portal) loaded after the startup
// loadCatalogPriceOverrides() pass has already run; this matches the app's
// established "correct next time the page loads" model (no Realtime
// anywhere). The CRM tab itself re-renders through applyOverrideLocally +
// its own local state, so the editor never looks stale.
//
// Override identity: `catalog` is one of the four ids below; `key` is the
// entry's `model` string — EXCEPT where `model` can't uniquely address an
// entry within its catalog (UNIFI_CATALOG's two rack cabinets both carry
// the placeholder model '—'), where the entry's `product` string is the
// key instead. overrideKeyFor() is the single source of that decision —
// the CRM tab derives each row's key through it, so the key written on
// edit and the key matched on load can never disagree. Unknown catalog/key
// rows (an entry later renamed/removed from the hardcoded array) are
// skipped silently — a stale override must never throw at app startup.
const CATALOGS = {
    camera: CAMERA_CATALOG,
    unifi: UNIFI_CATALOG,
    unifi_access: UNIFI_ACCESS_READER_CATALOG,
    ffe: FFE_EQUIPMENT_CATALOG,
};

// Per-catalog Map(key -> entry), plus a WeakMap(entry -> key) so the UI can
// ask for a row's key without re-deriving the duplicate-model logic.
const keyIndexes = {};
const entryKeys = new WeakMap();

function indexFor(catalogId) {
    if (keyIndexes[catalogId]) return keyIndexes[catalogId];
    const entries = CATALOGS[catalogId];
    if (!entries) return null;
    const modelCounts = new Map();
    entries.forEach((e) => {
        const m = (e.model || '').trim();
        modelCounts.set(m, (modelCounts.get(m) || 0) + 1);
    });
    const map = new Map();
    entries.forEach((e) => {
        const m = (e.model || '').trim();
        const modelUsable = m && m !== '—' && modelCounts.get(m) === 1;
        const key = modelUsable ? m : (e.product || m);
        entryKeys.set(e, key);
        if (!map.has(key)) map.set(key, e);
    });
    keyIndexes[catalogId] = map;
    return map;
}

// The override key for a catalog entry — what the CRM tab writes as `key`
// and what loadCatalogPriceOverrides matches against.
export function overrideKeyFor(catalogId, entry) {
    indexFor(catalogId); // ensure keys are computed
    return entryKeys.get(entry) || (entry && entry.model) || '';
}

// First-touch record of each entry's ORIGINAL hardcoded price, keyed
// `${catalog} ${key}` — recorded before the first mutation ever lands
// on that entry, never updated after, so "reset to default" always means
// the price shipped in code, not an earlier override.
const originalPrices = new Map();
const origKey = (catalogId, key) => `${catalogId} ${key}`;

// The entry's hardcoded (pre-override) price — falls back to the entry's
// current price when it has never been touched, and null for unknown
// catalog/key.
export function originalPriceFor(catalogId, key) {
    const recorded = originalPrices.get(origKey(catalogId, key));
    if (recorded !== undefined) return recorded;
    const entry = indexFor(catalogId)?.get(key);
    return entry ? entry.priceUsd : null;
}

// Mutates the matching entry's priceUsd in place (recording the original
// first). Returns true if an entry was found and updated.
export function applyOverrideLocally(catalogId, key, price) {
    const entry = indexFor(catalogId)?.get(key);
    const value = Number(price);
    if (!entry || !Number.isFinite(value) || value < 0) return false;
    const ok = origKey(catalogId, key);
    if (!originalPrices.has(ok)) originalPrices.set(ok, entry.priceUsd);
    entry.priceUsd = value;
    return true;
}

// Puts the entry's hardcoded price back (no-op if it was never overridden).
export function revertOverrideLocally(catalogId, key) {
    const entry = indexFor(catalogId)?.get(key);
    if (!entry) return false;
    const recorded = originalPrices.get(origKey(catalogId, key));
    if (recorded !== undefined) entry.priceUsd = recorded;
    return true;
}

// Fetches every override row and applies each to its in-memory catalog
// entry. Called fire-and-forget once at app startup (App.js) and again on
// CatalogPricingTab mount (idempotent — re-applying the same values is
// harmless, and originals are only recorded on first touch). Rows whose
// catalog/key no longer matches a real entry are skipped. Throws on a
// fetch error — callers .catch() and log.
export async function loadCatalogPriceOverrides(supabase) {
    const { data, error } = await supabase
        .from('buildlab_catalog_price_overrides')
        .select('catalog, key, price_usd');
    if (error) throw error;
    let applied = 0;
    (data || []).forEach((row) => {
        if (applyOverrideLocally(row.catalog, row.key, row.price_usd)) applied += 1;
    });
    return applied;
}
